import { Placement } from '@workday/canvas-kit-react/popup';
import { createStyles } from '@workday/canvas-kit-styling';
import { checkIcon, exclamationCircleIcon, exclamationTriangleIcon, infoIcon } from '@workday/canvas-system-icons-web';
import { system } from '@workday/canvas-tokens-web';
import { CanvasSystemIcon } from '@workday/design-assets-types';
import { useIntl } from '@workday/i18n/nextgen';
import { WcpStyles } from '@workday/wcp-tokens';
import * as React from 'react';
import { Canvas } from '../Canvas';
import DevsiteLink from './DevsiteLink';

/** The standard toast types. Each one sets the toast's icon and icon color. */
export type ToastVariant = 'success' | 'info' | 'warn' | 'error';

interface ToastButtonAction {
  text: string;
  onClick: () => void;
  url?: undefined;
}

interface ToastLinkAction {
  text: string;
  url: string;
  onClick?: () => void;
}

export type DevsiteToastAction = ToastButtonAction | ToastLinkAction;

export interface DevsiteToastProps {
  variant: ToastVariant;
  /**
   * Replaces the variant's icon. Reserve for truly unique cases; prefer a standard variant.
   */
  icon?: CanvasSystemIcon;
  /**
   * Replaces the variant's icon color.
   */
  iconColor?: string;
  mode?: 'alert' | 'dialog' | 'status';
  onClose?: () => void;
  action?: DevsiteToastAction;
  /**
   * @default 'top'
   */
  transformOrigin?: Placement;
  hideCloseIcon?: boolean;
}

const DevsiteToast: React.FC<React.PropsWithChildren<DevsiteToastProps>> = (props) => {
  const intl = useIntl();

  const CLOSE_ARIA_LABEL = intl.formatMessage({
    id: 'common.DevsiteToast.close.ariaLabel',
    defaultMessage: 'Close',
    description: 'ARIA label for toast close button',
  });

  const { variant, onClose, action, icon, iconColor, mode, hideCloseIcon = false, ...elemProps } = props;
  const isInteractive = onClose || action;
  const variantPreset = TOAST_VARIANT_PRESETS[variant];

  return (
    <Canvas.Toast cs={toastStyles} mode={isInteractive ? 'dialog' : mode} {...elemProps}>
      <Canvas.Toast.Icon
        data-cy='toast-icon'
        icon={icon ?? variantPreset.icon}
        color={iconColor ?? variantPreset.iconColor}
      />
      <Canvas.Toast.Body flexGrow={1}>
        <Canvas.Toast.Message data-cy='toast-message' as={typeof props.children === 'string' ? 'p' : 'div'}>
          {/* The types for this will not resolve due to an expansion of default types in React 19, which
              conflicts with the 'as' prop in Toast.Message, because it is typed as 'p' as default, but
              since we also make it a 'div', TS can't reconcile everything correctly. You can see the
              difference if you remove the as entirely, which defaults to 'p'. Will fix in DEVEXSITE-5961.*/}
          {props.children}
        </Canvas.Toast.Message>
        {action &&
          (isToastLinkAction(action) ? (
            <DevsiteLink url={action.url} onClick={action.onClick}>
              {action.text}
            </DevsiteLink>
          ) : (
            <Canvas.Toast.Link as='button' type='button' cs={toastActionButtonStyles} onClick={action.onClick}>
              {action.text}
            </Canvas.Toast.Link>
          ))}
      </Canvas.Toast.Body>
      {!hideCloseIcon && onClose && (
        <Canvas.Toast.CloseIcon data-cy='toast-close-icon' aria-label={CLOSE_ARIA_LABEL} onClick={onClose} />
      )}
    </Canvas.Toast>
  );
};

const TOAST_VARIANT_PRESETS: Record<ToastVariant, { icon: CanvasSystemIcon; iconColor: string }> = {
  success: { icon: checkIcon, iconColor: WcpStyles.color.icon.positive },
  info: { icon: infoIcon, iconColor: WcpStyles.color.icon.primary },
  warn: { icon: exclamationTriangleIcon, iconColor: WcpStyles.color.icon.caution.default },
  error: { icon: exclamationCircleIcon, iconColor: WcpStyles.color.icon.critical },
};

const isToastLinkAction = (action: DevsiteToastAction): action is ToastLinkAction => typeof action.url === 'string';

const toastStyles = createStyles({
  margin: 'auto',
  marginBottom: system.space.x4,
  whiteSpace: 'pre-wrap',
});

const toastActionButtonStyles = createStyles({
  appearance: 'none',
  background: 'none',
  border: 'none',
  font: 'inherit',
});

export default DevsiteToast;
